import { useEffect, useState } from "react";
import { api, INTENT_COLORS, fmtUsd, fmtTimeAgo } from "@/lib/nexus";
import { Sparkles, Loader2 } from "lucide-react";

const INTENTS = [
  "api-payment", "data-purchase", "compute-lease", "service-fee",
  "tip", "collusion", "drain", "model-inference", "orchestration", "stablecoin-settle",
];

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ intent: "", chain: "", min_usd: "" });
  const [selected, setSelected] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
    params.append("limit", "150");
    api.get(`/transactions?${params}`).then((r) => setItems(r.data.items));
  }, [filters]);

  const analyze = async (tx) => {
    setSelected(tx);
    setAnalysis(null);
    setLoading(true);
    try {
      const r = await api.post("/analyze/transaction", { transaction_id: tx.id });
      setAnalysis(r.data.analysis);
    } catch (e) {
      setAnalysis("Analysis unavailable. LLM error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <div className="panel-title">ON-CHAIN AGENT-TO-AGENT TRANSACTIONS</div>
        <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1">
          {items.length} transactions <span className="text-nexus-cyan">mapped to designed intent</span>
        </h1>
      </div>

      <div className="panel p-3 flex flex-wrap gap-2 items-center">
        <span className="panel-title">Intent</span>
        <select
          data-testid="tx-filter-intent"
          value={filters.intent}
          onChange={(e) => setFilters({ ...filters, intent: e.target.value })}
          className="bg-nexus-bg border border-nexus-border text-xs font-mono uppercase tracking-widest px-2 py-1"
        >
          <option value="">ALL INTENTS</option>
          {INTENTS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <span className="panel-title ml-3">Min USD</span>
        <input
          data-testid="tx-filter-min-usd"
          type="number"
          value={filters.min_usd}
          onChange={(e) => setFilters({ ...filters, min_usd: e.target.value })}
          className="bg-nexus-bg border border-nexus-border text-xs font-mono px-2 py-1 w-24"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 panel overflow-auto max-h-[70vh]">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th><th>Hash</th><th>Chain</th><th>From → To</th><th>Intent</th><th>Value</th><th>Conf</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} data-testid={`tx-row-${t.id}`}>
                  <td className="text-nexus-text-2">{fmtTimeAgo(t.timestamp)}</td>
                  <td className="text-nexus-cyan">{t.short_hash}</td>
                  <td>{t.chain}</td>
                  <td className="text-nexus-text">{t.from_name} → {t.to_name}</td>
                  <td>
                    <span
                      className="text-[10px] uppercase tracking-widest border px-1.5 py-0.5"
                      style={{ borderColor: INTENT_COLORS[t.intent], color: INTENT_COLORS[t.intent] }}
                    >
                      {t.intent_label}
                    </span>
                  </td>
                  <td className="text-nexus-text">{fmtUsd(t.value_usd)}</td>
                  <td>{(t.confidence * 100).toFixed(0)}%</td>
                  <td>
                    <button
                      data-testid={`tx-analyze-${t.id}`}
                      onClick={() => analyze(t)}
                      className="text-[10px] font-mono uppercase text-nexus-violet hover:text-nexus-cyan flex items-center gap-1"
                    >
                      <Sparkles size={11} /> AI
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel p-3 brackets">
          <span className="b1" /><span className="b2" />
          <div className="panel-title"><span className="dot" style={{ background: "#9D4EDD", boxShadow: "0 0 8px #9D4EDD" }} />AI Intent Correlation</div>
          {!selected && (
            <div className="text-[12px] text-nexus-text-2 mt-3">
              Select any transaction and click <span className="text-nexus-violet">AI</span> to correlate the on-chain event against the sender &amp; receiver&apos;s declared agent framework and expected commerce intent.
            </div>
          )}
          {selected && (
            <div className="mt-3 space-y-2 text-[12px]">
              <div className="hash">{selected.hash}</div>
              <div className="text-nexus-text-2">
                <span className="text-nexus-text">{selected.from_name}</span> → <span className="text-nexus-text">{selected.to_name}</span>
              </div>
              <div className="text-nexus-text-2">Value: <span className="text-nexus-text">{fmtUsd(selected.value_usd)}</span> · Chain: {selected.chain} · Method: <span className="text-nexus-cyan font-mono">{selected.method}</span></div>
              <div className="border-t border-nexus-border pt-3 mt-3">
                {loading && (
                  <div className="flex items-center gap-2 text-nexus-violet">
                    <Loader2 className="animate-spin" size={14} /> <span className="text-[11px] font-mono uppercase tracking-widest">Correlating intent…</span>
                  </div>
                )}
                {analysis && !loading && (
                  <div data-testid="tx-analysis-output" className="text-[12px] leading-relaxed text-nexus-text whitespace-pre-line">{analysis}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
